import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { AlertCircle, Mail, Lock, Eye, EyeOff, LogIn, ArrowLeft, ChevronLeft } from 'lucide-react'

const DarkLoginPage = () => {
  const [step, setStep] = useState(1)
  const [formData, setFormData] = useState({ email: '', password: '' })
  const [errors, setErrors] = useState({})
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')
  const [verificationEmail, setVerificationEmail] = useState('')
  const [resendLoading, setResendLoading] = useState(false)

  const { login, verifyLogin, resendVerificationCode } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  // Ook ?redirect=/pad ondersteunen (o.a. vanaf /pakketten), naast state.from
  const redirectParam = searchParams.get('redirect')
  const from = location.state?.from?.pathname || (redirectParam && redirectParam.startsWith('/') ? redirectParam : null) || '/dashboard'

  useEffect(() => {
    const error = searchParams.get('error')
    const message = searchParams.get('message')
    if (error === 'role_mismatch' && message) {
      setErrors({ general: decodeURIComponent(message) })
    } else if (error === 'oauth_failed') {
      setErrors({ general: 'Google login mislukt. Probeer het opnieuw.' })
    } else if (error === 'session_failed') {
      setErrors({ general: 'Sessie fout. Probeer het opnieuw.' })
    }
  }, [searchParams])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }))
  }

  const validateForm = () => {
    const newErrors = {}
    if (!formData.email) newErrors.email = 'E-mailadres is verplicht'
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Ongeldig e-mailadres'
    if (!formData.password) newErrors.password = 'Wachtwoord is verplicht'
    return newErrors
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const formErrors = validateForm()
    if (Object.keys(formErrors).length > 0) { setErrors(formErrors); return }

    setLoading(true); setErrors({})
    try {
      const result = await login(formData.email, formData.password)
      if (result && result.requiresVerification) {
        setVerificationEmail(result.email || formData.email)
        setStep(2)
        setLoading(false)
        return
      }
      if (result.success) {
        navigate(from, { replace: true })
      } else {
        setErrors({ general: result.message || 'Er is een fout opgetreden bij het inloggen' })
      }
    } catch (error) {
      if (error.response?.status === 401) setErrors({ general: 'Ongeldig e-mailadres of wachtwoord' })
      else if (error.response?.data?.message) setErrors({ general: error.response.data.message })
      else setErrors({ general: 'Er is een fout opgetreden. Probeer het opnieuw.' })
    } finally {
      setLoading(false)
    }
  }

  const handleVerificationSubmit = async (e) => {
    e.preventDefault()
    if (!verificationCode || verificationCode.length !== 6) {
      setErrors({ verification: 'Voer een geldige 6-cijferige code in' }); return
    }
    setLoading(true); setErrors({})
    try {
      await verifyLogin(verificationEmail, verificationCode, 'login')
      navigate(from, { replace: true })
    } catch (error) {
      if (error.response?.data?.message) setErrors({ verification: error.response.data.message })
      else setErrors({ verification: 'Ongeldige verificatiecode. Probeer het opnieuw.' })
    } finally {
      setLoading(false)
    }
  }

  const handleResendCode = async () => {
    setResendLoading(true); setErrors({})
    try {
      await resendVerificationCode(verificationEmail, 'login')
      setErrors({ verification: 'Een nieuwe code is verstuurd naar je email' })
    } catch (error) {
      setErrors({ verification: 'Kon geen nieuwe code versturen. Probeer het opnieuw.' })
    } finally {
      setResendLoading(false)
    }
  }

  const handleBackToForm = () => {
    setStep(1)
    setVerificationCode('')
    setErrors({})
  }

  const isPositiveVerification = errors.verification && errors.verification.includes('verstuurd')

  const GoogleIcon = () => (
    <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09Z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
      <path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84Z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
    </svg>
  )

  return (
    <div className="font-['Geist',ui-sans-serif,system-ui] min-h-screen flex bg-white text-[#0A0F0D] antialiased">
      <style>{`
        @keyframes ugc-pulse { 0%{transform:scale(1);opacity:.5} 100%{transform:scale(2.2);opacity:0} }
        @keyframes ugc-scrollLeft { from{transform:translateX(0)} to{transform:translateX(-50%)} }
        @keyframes ugc-scrollRight { from{transform:translateX(-50%)} to{transform:translateX(0)} }
        .ugc-pulse-dot::before { content:""; position:absolute; inset:0; border-radius:9999px; background:#A7F3D0; opacity:.4; animation: ugc-pulse 2.2s infinite; }
        .ugc-marquee-mask { -webkit-mask-image: linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent); mask-image: linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent); }
        .ugc-track-1 { animation: ugc-scrollLeft 38s linear infinite; }
        .ugc-track-2 { animation: ugc-scrollRight 38s linear infinite; }
        .ugc-trustpilot-star::before { content:""; width:18px; height:18px; background:#00B67A; border-radius:3px; display:inline-block; background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23fff'><polygon points='12 2 15 9 22 9.5 17 14.5 18.5 22 12 18 5.5 22 7 14.5 2 9.5 9 9'/></svg>"); background-size:contain; background-repeat:no-repeat; background-position:center; }
      `}</style>

      {/* ===== ASIDE (left) ===== */}
      <aside className="hidden lg:flex relative flex-[1.05] flex-col justify-between overflow-hidden text-white rounded-r-[48px] min-h-screen pt-12"
             style={{ background: 'linear-gradient(160deg,#047857 0%,#059669 45%,#065F46 100%)' }}>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-[300px] -right-[300px] w-[900px] h-[900px] rounded-full blur-[40px]"
               style={{ background: 'radial-gradient(circle, rgba(167,243,208,.35), transparent 60%)' }} />
          <div className="absolute -bottom-[200px] -left-[200px] w-[700px] h-[700px] rounded-full blur-[60px]"
               style={{ background: 'radial-gradient(circle, rgba(139,92,246,.25), transparent 60%)' }} />
          <div className="absolute -top-[120px] -right-[180px] w-[520px] h-[520px] grid place-items-center font-extrabold leading-[0.9] -rotate-[22deg] select-none pointer-events-none"
               style={{ fontSize: 520, color: 'rgba(255,255,255,.08)' }} aria-hidden="true">U</div>
          <div className="absolute inset-0 mix-blend-soft-light opacity-[0.08] pointer-events-none"
               style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '5px 5px' }} />
        </div>

        <Link to="/" className="absolute top-8 left-12 z-10 flex items-center gap-2.5 font-bold tracking-tight text-[19px] text-white">
          <span className="w-8 h-8 rounded-[10px] grid place-items-center font-extrabold text-sm border border-white/20 backdrop-blur-md"
                style={{ background: 'rgba(255,255,255,.15)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.2)' }}>U</span>
          UGC<b className="text-[#A7F3D0] font-extrabold">4you</b>
        </Link>

        <div className="relative z-[2] flex-1 flex flex-col items-center justify-center gap-10 px-14 pt-20 pb-8">
          <div className="inline-flex items-center gap-2.5 pl-1.5 pr-3.5 py-1.5 rounded-full text-[13px] font-medium text-white border border-white/20 backdrop-blur-md"
               style={{ background: 'rgba(255,255,255,.1)' }}>
            <span className="relative w-[18px] h-[18px] rounded-full grid place-items-center ugc-pulse-dot" style={{ background: 'rgba(255,255,255,.2)' }}>
              <span className="w-[7px] h-[7px] rounded-full bg-[#A7F3D0]" />
            </span>
            1000+ creators &amp; merken samen
          </div>

          <h1 className="text-center font-semibold leading-[1.05] tracking-[-0.032em] max-w-[540px]"
              style={{ fontSize: 'clamp(36px,3.6vw,52px)' }}>
            Brengt <span className="text-[#A7F3D0] font-bold">creators</span> en<br />
            <span className="text-[#A7F3D0] font-bold">bedrijven</span> samen.
          </h1>

          <div className="flex items-center gap-[18px] py-3.5 px-5 rounded-2xl border border-white/15 backdrop-blur-md"
               style={{ background: 'rgba(255,255,255,.08)' }}>
            <div className="flex flex-col items-start gap-1">
              <span className="ugc-trustpilot-star inline-flex items-center gap-1.5 font-bold text-white text-[13.5px]">Trustpilot</span>
            </div>
            <div className="w-px h-9 bg-white/20" />
            <div className="flex flex-col gap-0.5">
              <div className="text-[15px] font-semibold text-white flex items-baseline gap-1">
                4.6 <em className="not-italic text-white/50 font-normal text-[13px]">/ 5.0</em>
              </div>
              <div className="text-[#FBBF24] text-[13px] tracking-[1.5px]">★★★★★</div>
            </div>
          </div>
        </div>

        <div className="relative z-[2] flex flex-col gap-3.5 mb-6">
          <div className="overflow-hidden ugc-marquee-mask">
            <div className="ugc-track-1 flex gap-12 w-max items-center">
              {[
                ['Magnum','bold'],['DYSON','thin'],['Starbucks','serif'],["L'Oréal",'bold'],
                ['ABN AMRO','mono'],['COLGATE','thin'],['Sophia Mae','serif'],["Kellogg's",'bold'],
                ['Magnum','bold'],['DYSON','thin'],['Starbucks','serif'],["L'Oréal",'bold'],
                ['ABN AMRO','mono'],['COLGATE','thin'],['Sophia Mae','serif'],["Kellogg's",'bold'],
              ].map(([name, kind], i) => <Brand key={i} name={name} kind={kind} />)}
            </div>
          </div>
          <div className="overflow-hidden ugc-marquee-mask">
            <div className="ugc-track-2 flex gap-12 w-max items-center">
              {[
                ['HEMA','mono'],['Albert Heijn','serif'],['Bol.com','bold'],['RITUALS','thin'],
                ['Coolblue','serif'],['Jumbo','bold'],['DECATHLON','thin'],['KPN','mono'],
                ['HEMA','mono'],['Albert Heijn','serif'],['Bol.com','bold'],['RITUALS','thin'],
                ['Coolblue','serif'],['Jumbo','bold'],['DECATHLON','thin'],['KPN','mono'],
              ].map(([name, kind], i) => <Brand key={i} name={name} kind={kind} />)}
            </div>
          </div>
        </div>

        <div className="relative z-[2] mx-auto w-[90%] max-w-[680px] max-h-[46vh] flex justify-center items-end">
          <div className="absolute inset-x-[-2%] inset-y-0 rounded-3xl rotate-[3.5deg] z-0" style={{ background: 'rgba(255,255,255,.18)' }} />
          <div className="relative z-[1] w-full bg-white rounded-t-3xl border-[5px] border-b-0 border-white/70 overflow-hidden shadow-[0_30px_60px_-20px_rgba(0,0,0,0.4)]" style={{ aspectRatio: '1573/1000' }}>
            <DashboardMock />
          </div>
        </div>
      </aside>

      {/* ===== MAIN (right) ===== */}
      <main className="flex-1 min-w-0 w-full flex flex-col min-h-screen bg-white relative">
        <div className="px-5 py-4 sm:px-8 sm:py-6 flex justify-end">
          <Link to="/" className="inline-flex items-center gap-2 text-[#64748B] text-sm font-medium px-3.5 py-2 rounded-lg hover:bg-[#F1F3F5] hover:text-[#0A0F0D] transition">
            <ChevronLeft className="w-3.5 h-3.5" />
            Terug naar home
          </Link>
        </div>

        <div className="flex-1 flex flex-col justify-center items-center px-5 sm:px-8 pb-12">
          <div className="w-full max-w-[420px]">
            <Link to="/" className="lg:hidden flex items-center justify-center gap-2.5 font-bold tracking-tight text-[22px] mb-8">
              <span className="w-8 h-8 rounded-[10px] grid place-items-center font-extrabold text-sm text-white bg-[#10B981] shadow-[0_4px_10px_-3px_rgba(16,185,129,0.5)]">U</span>
              UGC<b className="text-[#059669] font-extrabold">4you</b>
            </Link>

            {step === 1 ? (
              <div className="text-center mb-8">
                <h1 className="text-[30px] sm:text-[36px] font-bold tracking-[-0.03em] leading-[1.1] text-[#0A0F0D]">Welkom terug</h1>
                <p className="mt-2 text-[#64748B] text-[15px] leading-relaxed">Log in op je account om verder te gaan</p>
              </div>
            ) : (
              <div className="text-center mb-8">
                <h1 className="text-[28px] sm:text-[32px] font-bold tracking-[-0.03em] leading-[1.1] text-[#0A0F0D]">Verificatiecode</h1>
                <p className="mt-2 text-[#64748B] text-[15px] leading-relaxed">
                  We hebben een 6-cijferige code naar <strong className="text-[#0A0F0D]">{verificationEmail}</strong> gestuurd
                </p>
              </div>
            )}

            <div className="bg-white border border-[#E5E7EB] rounded-[20px] p-5 sm:p-7 shadow-[0_1px_2px_rgba(0,0,0,0.03),0_8px_24px_-12px_rgba(0,0,0,0.06)]">
              {(errors.general || errors.verification) && (
                <div className={`mb-5 p-3.5 border rounded-lg flex gap-2.5 items-start ${
                  isPositiveVerification ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
                }`}>
                  <AlertCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isPositiveVerification ? 'text-emerald-500' : 'text-red-500'}`} />
                  <p className={`text-sm ${isPositiveVerification ? 'text-emerald-800' : 'text-red-800'}`}>
                    {errors.general || errors.verification}
                  </p>
                </div>
              )}

              {step === 1 ? (
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label htmlFor="email" className="block text-[13.5px] font-semibold text-[#1F2937] mb-1.5">E-mailadres</label>
                    <div className={`flex items-center bg-white border rounded-[10px] transition focus-within:border-[#10B981] focus-within:ring-4 focus-within:ring-[#D1FAE5] ${errors.email ? 'border-red-300' : 'border-[#E5E7EB]'}`}>
                      <Mail className="w-[18px] h-[18px] text-[#64748B] ml-3.5 flex-shrink-0" />
                      <input
                        id="email" name="email" type="email" autoComplete="email" required
                        value={formData.email} onChange={handleChange}
                        placeholder="jouw.email@voorbeeld.nl"
                        className="flex-1 min-w-0 w-full px-3 py-3.5 bg-transparent border-0 outline-none text-base sm:text-[14.5px] text-[#0A0F0D] placeholder:text-[#94A3B8]"
                      />
                    </div>
                    {errors.email && <p className="mt-1.5 text-xs text-red-600">{errors.email}</p>}
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-[13.5px] font-semibold text-[#1F2937] mb-1.5">Wachtwoord</label>
                    <div className={`flex items-center bg-white border rounded-[10px] transition focus-within:border-[#10B981] focus-within:ring-4 focus-within:ring-[#D1FAE5] ${errors.password ? 'border-red-300' : 'border-[#E5E7EB]'}`}>
                      <Lock className="w-[18px] h-[18px] text-[#64748B] ml-3.5 flex-shrink-0" />
                      <input
                        id="password" name="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password" required
                        value={formData.password} onChange={handleChange}
                        placeholder="Je wachtwoord"
                        className="flex-1 min-w-0 w-full px-3 py-3.5 bg-transparent border-0 outline-none text-base sm:text-[14.5px] text-[#0A0F0D] placeholder:text-[#94A3B8]"
                      />
                      <button type="button" aria-label={showPassword ? 'Verberg wachtwoord' : 'Toon wachtwoord'}
                              onClick={() => setShowPassword(!showPassword)}
                              className="py-2 px-3.5 text-[#64748B] hover:text-[#0A0F0D]">
                        {showPassword ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
                      </button>
                    </div>
                    {errors.password && <p className="mt-1.5 text-xs text-red-600">{errors.password}</p>}
                  </div>

                  <div className="flex flex-wrap justify-between items-center gap-x-4 gap-y-1 my-4">
                    <label className="inline-flex items-center gap-2 py-2 text-[13.5px] font-medium text-[#1F2937] cursor-pointer select-none">
                      <input type="checkbox" id="remember-me" className="peer absolute opacity-0 pointer-events-none" />
                      <span className="w-[18px] h-[18px] rounded-[5px] border-[1.5px] border-[#E5E7EB] bg-white grid place-items-center transition peer-checked:bg-[#10B981] peer-checked:border-[#10B981] peer-checked:after:content-[''] peer-checked:after:w-[9px] peer-checked:after:h-[5px] peer-checked:after:border-2 peer-checked:after:border-white peer-checked:after:border-t-0 peer-checked:after:border-r-0 peer-checked:after:-rotate-45 peer-checked:after:translate-x-[1px] peer-checked:after:-translate-y-[1px]" />
                      Onthoud mij
                    </label>
                    <Link to="/forgot-password" className="inline-flex items-center py-2 text-[13.5px] font-semibold text-[#047857] hover:text-[#065F46] hover:underline">
                      Wachtwoord vergeten?
                    </Link>
                  </div>

                  <button type="submit" disabled={loading}
                          className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-5 rounded-[11px] bg-[#10B981] hover:bg-[#059669] text-white text-[15px] font-semibold tracking-tight transition transform hover:-translate-y-[1px] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                          style={{ boxShadow: '0 1px 0 rgba(255,255,255,.2) inset, 0 1px 2px rgba(16,185,129,.2), 0 6px 18px -4px rgba(16,185,129,.55)' }}>
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <LogIn className="w-4 h-4" />
                        Inloggen
                      </>
                    )}
                  </button>

                  <div className="flex items-center gap-3.5 my-5 text-[13px] text-[#64748B] font-medium">
                    <div className="flex-1 h-px bg-[#E5E7EB]" />
                    Of log in met Google
                    <div className="flex-1 h-px bg-[#E5E7EB]" />
                  </div>

                  <div className="grid grid-cols-2 gap-2.5">
                    <button type="button"
                            onClick={() => { window.location.href = `${import.meta.env.VITE_API_URL || 'https://ugc4you.nl/api'}/auth/google/creator` }}
                            className="inline-flex items-center justify-center gap-2 py-2.5 px-3.5 bg-white text-[#1F2937] border border-[#E5E7EB] rounded-[10px] text-sm font-semibold hover:border-[#1F2937] hover:bg-[#FAFAF7] hover:-translate-y-[1px] hover:shadow-[0_4px_10px_-4px_rgba(0,0,0,0.1)] transition">
                      <GoogleIcon /> Creator
                    </button>
                    <button type="button"
                            onClick={() => { window.location.href = `${import.meta.env.VITE_API_URL || 'https://ugc4you.nl/api'}/auth/google/bedrijf` }}
                            className="inline-flex items-center justify-center gap-2 py-2.5 px-3.5 bg-white text-[#1F2937] border border-[#E5E7EB] rounded-[10px] text-sm font-semibold hover:border-[#1F2937] hover:bg-[#FAFAF7] hover:-translate-y-[1px] hover:shadow-[0_4px_10px_-4px_rgba(0,0,0,0.1)] transition">
                      <GoogleIcon /> Bedrijf
                    </button>
                  </div>

                  <div className="mt-5 text-center text-sm text-[#64748B]">
                    Nog geen account?{' '}
                    <Link to="/register" className="font-semibold text-[#047857] hover:text-[#065F46] hover:underline">
                      Start je UGC reis hier
                    </Link>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleVerificationSubmit} className="space-y-5">
                  <button type="button" onClick={handleBackToForm}
                          className="flex items-center text-sm text-[#64748B] hover:text-[#0A0F0D]">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Terug naar login
                  </button>

                  <div>
                    <label htmlFor="verificationCode" className="block text-[13.5px] font-semibold text-[#1F2937] mb-3">
                      Voer je verificatiecode in
                    </label>
                    <input
                      id="verificationCode" name="verificationCode" type="text" inputMode="numeric"
                      maxLength="6" autoComplete="off" autoFocus
                      value={verificationCode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '')
                        setVerificationCode(value)
                        if (errors.verification) setErrors({})
                      }}
                      placeholder="000000"
                      className="w-full min-w-0 px-3 py-4 border border-[#E5E7EB] rounded-[10px] outline-none text-center text-2xl tracking-[0.5em] font-mono text-[#0A0F0D] placeholder:text-[#94A3B8] focus:border-[#10B981] focus:ring-4 focus:ring-[#D1FAE5] transition"
                    />
                    <p className="mt-2 text-xs text-[#94A3B8] text-center">De code is 10 minuten geldig</p>
                  </div>

                  <button type="submit" disabled={loading || verificationCode.length !== 6}
                          className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-5 rounded-[11px] bg-[#10B981] hover:bg-[#059669] text-white text-[15px] font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ boxShadow: '0 1px 0 rgba(255,255,255,.2) inset, 0 6px 18px -4px rgba(16,185,129,.55)' }}>
                    {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Verifiëren'}
                  </button>

                  <div className="text-center">
                    <button type="button" onClick={handleResendCode} disabled={resendLoading}
                            className="text-sm font-semibold text-[#047857] hover:text-[#065F46] disabled:opacity-50">
                      {resendLoading ? 'Versturen...' : 'Code opnieuw versturen'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>

        <div className="px-5 sm:px-8 py-4 sm:py-5 border-t border-[#F1F3F5] flex flex-col sm:flex-row gap-2 justify-between items-center text-xs text-[#64748B]">
          <span className="py-1">© 2026 UGC4you</span>
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-1">
            <Link to="/privacy-policy" className="inline-flex items-center py-2 hover:text-[#0A0F0D]">Privacy</Link>
            <Link to="/algemene-voorwaarden" className="inline-flex items-center py-2 hover:text-[#0A0F0D]">Voorwaarden</Link>
            <a href="mailto:support@ugc4you.nl" className="inline-flex items-center py-2 hover:text-[#0A0F0D]">Support</a>
          </div>
        </div>
      </main>
    </div>
  )
}

// ---- Sub-components ----

const Brand = ({ name, kind }) => {
  const cls = {
    bold: 'font-extrabold tracking-[-0.04em] text-[22px]',
    thin: 'font-light tracking-[0.04em] uppercase text-[18px]',
    serif: "font-normal italic text-[22px] font-['Georgia',serif]",
    mono: "font-semibold tracking-[-0.04em] text-[20px] font-['ui-monospace',monospace]",
  }[kind] || 'font-extrabold text-[22px]'
  return <span className={`flex-shrink-0 whitespace-nowrap text-white/40 ${cls}`}>{name}</span>
}

const DashboardMock = () => (
  <div className="relative w-full h-full flex text-[10px]" style={{ background: '#FAFAF7' }}>
    <div className="w-[18%] bg-white border-r border-[#E5E7EB] py-3.5 px-2.5 flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5 px-1.5 mb-2 font-bold text-[11px] text-[#0A0F0D]">
        <span className="w-3.5 h-3.5 bg-[#10B981] rounded inline-block" /> UGC<b className="text-[#059669]">4you</b>
      </div>
      {['Dashboard','Opdrachten','Creators','Berichten','Betalingen'].map((label, i) => (
        <div key={i} className={`flex items-center gap-1.5 px-2 py-1.5 rounded font-medium ${i === 0 ? 'bg-emerald-50 text-[#047857]' : 'text-[#64748B]'}`}>
          <span className="w-2 h-2 bg-current rounded-sm opacity-40" /> {label}
        </div>
      ))}
    </div>
    <div className="flex-1 py-3.5 px-4 flex flex-col gap-2.5 overflow-hidden">
      <div className="flex justify-between items-center">
        <h3 className="text-[13px] font-bold text-[#0A0F0D] tracking-tight">Welkom terug, Mark 👋</h3>
        <div className="bg-[#10B981] text-white px-3 py-1 rounded text-[9px] font-semibold">+ Nieuwe opdracht</div>
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        {[
          { l: 'Actieve opdrachten', v: '12', c: '#047857' },
          { l: 'Voorstellen', v: '48', c: '#7C3AED' },
          { l: 'Levertijd', v: '42u', c: '#0A0F0D' },
          { l: 'Besteed deze maand', v: '€2.4k', c: '#0A0F0D' },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-[#E5E7EB] rounded-lg py-2 px-2.5">
            <div className="text-[8px] text-[#64748B] font-medium">{s.l}</div>
            <div className="text-[14px] font-bold tracking-tight mt-0.5" style={{ color: s.c }}>{s.v}</div>
          </div>
        ))}
      </div>
      <div className="flex-1 grid grid-cols-[1.6fr_1fr] gap-2">
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-2.5 flex flex-col gap-1.5">
          <div className="text-[9px] font-semibold text-[#1F2937] flex justify-between">
            Opdrachten per week <span className="text-[#64748B] font-normal">Nov 2026</span>
          </div>
          <div className="flex-1 flex items-end gap-[3px] pt-1">
            {[40,62,48,78,55,82,96,70].map((h, i) => (
              <span key={i} className="flex-1 rounded-t-sm"
                    style={{
                      height: `${h}%`,
                      background: i === 7
                        ? 'linear-gradient(180deg, #8B5CF6 0%, #EDE9FE 100%)'
                        : 'linear-gradient(180deg, #10B981 0%, #A7F3D0 100%)',
                    }} />
            ))}
          </div>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-2.5 flex flex-col gap-1.5">
          <div className="text-[9px] font-semibold text-[#1F2937] flex justify-between">
            Top creators <span className="text-[#64748B] font-normal">Deze week</span>
          </div>
          {[
            { n: 'Luca', r: 'Lifestyle · Tech', s: '5.0', g: 'linear-gradient(135deg,#10B981,#059669)' },
            { n: 'Jaimie', r: 'Beauty', s: '5.0', g: 'linear-gradient(135deg,#8B5CF6,#EC4899)' },
            { n: 'Kaya', r: 'Food', s: '4.9', g: 'linear-gradient(135deg,#06B6D4,#10B981)' },
            { n: 'Eva', r: 'Fashion', s: '5.0', g: 'linear-gradient(135deg,#F59E0B,#EF4444)' },
          ].map((c, i) => (
            <div key={i} className="flex items-center gap-1.5 p-1 rounded">
              <span className="w-[18px] h-[18px] rounded-full flex-shrink-0" style={{ background: c.g }} />
              <div className="min-w-0">
                <div className="text-[9px] font-semibold text-[#0A0F0D] leading-tight">{c.n}</div>
                <div className="text-[8px] text-[#64748B]">{c.r}</div>
              </div>
              <div className="ml-auto text-[8px] text-[#047857] bg-emerald-50 px-1.5 py-0.5 rounded-full font-semibold">★ {c.s}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
)

export default DarkLoginPage
